// =============================================================================
// Pintravel — What the clock and the signal bars should look like
// =============================================================================
// The system bars used to follow the theme: light mode meant dark icons, dark
// mode meant light ones. That is right only while the thing behind them is the
// app's own background — and it stops being right the moment a screen puts
// something else there. A dark map in light mode gets dark icons on a dark map;
// a photo behind the status bar gets whatever the theme happened to say.
//
// What the bars actually need to know is the brightness of the pixels *behind
// them*, which only the screen drawing those pixels can answer. So screens
// declare it, and this holds the answer.
//
// A stack rather than a single value: screens open on top of one another and
// come back in any order, and the one that should win is the one currently on
// top — not whichever unmounted last.
// =============================================================================

import { create } from 'zustand';

/**
 * `light` and `dark` describe the *content behind the bars*, not the icons.
 * A photo is dark content and therefore needs light icons; naming it after the
 * icons is how these get set backwards.
 */
export type BarContent = 'light' | 'dark';

export interface SystemBarClaim {
  /** Identifies the screen that made the claim, so it can withdraw exactly its own. */
  id: string;
  content: BarContent;
  /**
   * Whether the web view should draw under the bars. Screens that put an image
   * up there want this; ordinary screens do not, or their headers slide beneath
   * the clock.
   */
  overlay: boolean;
}

interface SystemBarsState {
  claims: SystemBarClaim[];
  claim: (claim: SystemBarClaim) => void;
  release: (id: string) => void;
}

export const useSystemBarsStore = create<SystemBarsState>((set) => ({
  claims: [],

  claim: (claim) =>
    set((state) => ({
      // Replacing rather than appending, so a screen that re-declares on a
      // prop change does not stack up copies of itself.
      claims: [...state.claims.filter((existing) => existing.id !== claim.id), claim],
    })),

  release: (id) =>
    set((state) => ({ claims: state.claims.filter((existing) => existing.id !== id) })),
}));

/** The claim in force, or null when nothing has an opinion and the theme decides. */
export function selectActiveClaim(state: SystemBarsState): SystemBarClaim | null {
  return state.claims[state.claims.length - 1] ?? null;
}
